import type { ReactNode } from 'react';
import { Check, X } from 'lucide-react';
import { cx } from './cx';

/**
 * Pill toggle (aria-pressed), the prototype's choice chip. Selected and unselected are the same
 * width (the check takes the extra side padding), so selecting never reflows the row.
 */
export function ChoiceChip({ pressed, onClick, children, disabled }: { pressed: boolean; onClick: () => void; children: ReactNode; disabled?: boolean }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      className={cx(
        'inline-flex min-h-11 items-center gap-1.5 rounded-pill border text-sm font-medium transition-colors',
        // Same weight in both states; pl-3 + 14px check + 6px gap + pr-4 = px-6 (48px either way).
        pressed ? 'border-brand bg-brand-soft pl-3 pr-4 text-brand shadow-[inset_0_0_0_1px_rgb(var(--brand))]' : 'border-line-btn bg-white px-6 text-ink hover:border-brand hover:bg-brand-tint',
      )}
    >
      {pressed && <Check aria-hidden="true" size={14} strokeWidth={2.6} className="flex-none" />}
      {children}
    </button>
  );
}

type Opt = { value: string; label: string };
/** Fieldset of choice chips. Single choice when `value` is a string, multi when an array. */
export function ChoiceGroup({ legend, hint, options, value, onToggle, error }: {
  legend: ReactNode; hint?: ReactNode; options: Opt[]; value: string | string[] | null | undefined;
  onToggle: (value: string) => void; error?: string | null;
}) {
  const on = (v: string) => (Array.isArray(value) ? value.includes(v) : value === v);
  return (
    <fieldset className="m-0 min-w-0 border-0 p-0">
      <legend className={cx('p-0 text-[15px] font-semibold', hint ? 'mb-0.5' : 'mb-2.5')}>{legend}</legend>
      {hint && <div className="mb-2.5 text-sm text-muted">{hint}</div>}
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <ChoiceChip key={o.value} pressed={on(o.value)} onClick={() => onToggle(o.value)}>{o.label}</ChoiceChip>
        ))}
      </div>
      {error && <div className="mt-1.5 text-[13px] font-semibold text-bad">{error}</div>}
    </fieldset>
  );
}

/** Status pill with a leading dot ("Ready for review"). */
export type PillTone = 'brand' | 'info' | 'neutral' | 'ok' | 'warn' | 'purple' | 'bad';
const tones: Record<PillTone, string> = {
  brand: 'bg-brand-soft text-brand',
  info: 'bg-info-bg text-info-fg',
  neutral: 'bg-subtle text-ink-2',
  ok: 'bg-ok-bg text-ok-fg',
  warn: 'bg-warn-bg text-warn-fg',
  purple: 'bg-purple-bg text-purple-fg',
  bad: 'bg-bad-bg text-bad',
};
const dots: Record<PillTone, string> = {
  brand: 'bg-brand', info: 'bg-info-dot', neutral: 'bg-muted', ok: 'bg-ok', warn: 'bg-warn-dot', purple: 'bg-purple-fg', bad: 'bg-bad',
};
export function Pill({ tone = 'neutral', children, className, dot }: { tone?: PillTone; children: ReactNode; className?: string; dot?: boolean }) {
  return (
    <span className={cx('inline-flex w-fit items-center gap-1.5 rounded-pill px-2.5 py-[3px] text-[13px] font-semibold leading-[1.4]', tones[tone], className)}>
      {dot && <span aria-hidden="true" className={cx('h-[7px] w-[7px] flex-none rounded-full', dots[tone])} />}
      {children}
    </span>
  );
}

/** Small grey tag for tool names in tables ("Karbon"). */
export function Tag({ children }: { children: ReactNode }) {
  return <span className="inline-block rounded-md bg-subtle px-2 py-[3px] text-xs font-medium text-ink">{children}</span>;
}

/** Navy selection chip, optionally removable ("Karbon ×"). */
export function TokenChip({ children, muted, onRemove, removeLabel }: { children: ReactNode; muted?: boolean; onRemove?: () => void; removeLabel?: string }) {
  return (
    <span className={cx('inline-flex min-h-8 items-center gap-1 rounded-pill pl-3 text-sm font-semibold', onRemove ? 'pr-1' : 'pr-3', muted ? 'bg-subtle text-ink-2' : 'bg-brand-soft text-brand')}>
      {children}
      {onRemove && (
        <button type="button" aria-label={removeLabel} onClick={onRemove} className="flex h-7 w-7 items-center justify-center rounded-full border-0 bg-transparent p-0 text-current hover:bg-white/70">
          <X aria-hidden="true" size={14} strokeWidth={2.4} />
        </button>
      )}
    </span>
  );
}
